/**
 * Help, in the corner, on every screen.
 *
 * A question mark button and a four item menu: Docs, Keyboard shortcuts,
 * Replay the tour, Contact us. It is deliberately small. A help centre with a
 * search field and twelve categories is a help centre a person browses instead
 * of getting help, and three of these four items answer in one click.
 *
 * CONTACT US IS THE ONE THAT MATTERS. The Thai support story is the reason a
 * person in Bangkok picks this over Vercel, so the way to reach a human is a
 * standing item in the chrome rather than a page somebody has to go looking
 * for. It says which languages get answered, because "contact us" that turns
 * out to be English only is worse than no link at all.
 *
 * The handles below are prototype fixtures in one constant. They are the one
 * thing on this screen that has to be replaced with real values before this
 * ships, and they are gathered here so that replacement is one edit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { CopyField } from '../patterns/CopyField';
import { Dialog } from '../primitives/Dialog';
import { Button } from '../primitives/Button';
import { DOCS_URL } from '../patterns/CommandPalette';
import { t } from '../../lib/i18n/t';
import { startTour } from '../../lib/hooks/use-tour';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/omnibar.css';

/** Prototype fixtures. Replace with the real handles before this ships. */
const SUPPORT = {
  line: '@svcloud',
  email: 'support@sv-academy.org',
};

type Sheet = 'none' | 'keys' | 'contact';

interface ShortcutRow {
  what: string;
  keys: string;
}

const SHORTCUTS: ShortcutRow[] = [
  { what: 'Search projects, screens and things to do', keys: 'Cmd K' },
  { what: 'Close whatever is open', keys: 'Esc' },
  { what: 'Move to the next control', keys: 'Tab' },
  { what: 'Move through a list of results', keys: 'Up and down' },
  { what: 'Show or hide the demo controls', keys: 'D' },
];

export function HelpMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>('none');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus) buttonRef.current?.focus();
    },
    [],
  );

  /**
   * Opening a sheet from a menu item, without stranding the keyboard.
   *
   * The Dialog primitive returns focus, on close, to whatever was focused when
   * it opened. Left alone that is the menu item, which the same click just
   * unmounted, so closing the sheet drops focus onto the document body and a
   * keyboard user is back at the top of the page. Chasing it afterwards means
   * racing the primitive's own restore.
   *
   * So the trigger takes focus back BEFORE the sheet opens. The dialog then
   * records the question mark as its origin and its own restore does the right
   * thing, with no timers and no second opinion about where focus belongs.
   */
  const openSheet = useCallback((which: Sheet) => {
    captureEvent('help_menu_sheet_opened', { sheet: which });
    buttonRef.current?.focus();
    setOpen(false);
    setSheet(which);
  }, []);

  const closeSheet = useCallback(() => setSheet('none'), []);

  // A click anywhere else puts it away. A menu that needs its own button
  // pressed again to close is a menu that gets left open.
  useEffect(() => {
    if (!open) return;
    captureEvent('help_menu_opened');
    function onDown(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  const onTriggerKey = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  }, []);

  const onMenuKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const nodes = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );
      if (nodes.length === 0) return;
      const at = nodes.indexOf(document.activeElement as HTMLElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (at + step + nodes.length) % nodes.length;
      nodes[next].focus();
    },
    [close],
  );

  function replayTour() {
    captureEvent('help_tour_replayed');
    close(false);
    navigate('/projects');
    startTour();
  }

  function openDocs() {
    captureEvent('help_docs_opened');
    close(false);
    window.open(DOCS_URL, '_blank', 'noreferrer,noopener');
  }

  return (
    <div className="omni__anchor" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className="omni__icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Help')}
        data-tour="help"
        onClick={() => setOpen((was) => !was)}
        onKeyDown={onTriggerKey}
      >
        <svg viewBox="0 0 16 16" width="15" height="15" focusable="false" aria-hidden="true">
          <circle
            cx="8"
            cy="8"
            r="6.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
          />
          <path
            d="M6.2 6.2a1.9 1.9 0 1 1 2.4 1.9c-.4.15-.6.5-.6.95v.3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
          <circle cx="8" cy="11.4" r="0.75" fill="currentColor" />
        </svg>
      </button>

      {open ? (
        <div
          className="helpmenu"
          role="menu"
          aria-label={t('Help')}
          ref={menuRef}
          onKeyDown={onMenuKey}
        >
          <button
            type="button"
            role="menuitem"
            className="helpmenu__item"
            onClick={openDocs}
          >
            <span>{t('Docs')}</span>
            <span className="helpmenu__away">{t('opens in a new tab')}</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="helpmenu__item"
            onClick={() => openSheet('keys')}
          >
            <span>{t('Keyboard shortcuts')}</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="helpmenu__item"
            onClick={replayTour}
          >
            <span>{t('Replay the tour')}</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="helpmenu__item"
            onClick={() => openSheet('contact')}
          >
            <span>{t('Contact us')}</span>
          </button>
        </div>
      ) : null}

      <Dialog
        open={sheet === 'keys'}
        onClose={closeSheet}
        title={t('Keyboard shortcuts')}
        description={t('Everything in the console is reachable with the keyboard alone.')}
        footer={
          <Button variant="outline" onClick={closeSheet} data-autofocus>
            {t('Close')}
          </Button>
        }
      >
        <div className="helpsheet">
          <div className="helpsheet__ledger">
            {SHORTCUTS.map((row) => (
              <div className="helpsheet__row" key={row.keys}>
                <span>{t(row.what)}</span>
                <span className="helpsheet__keys">{t(row.keys)}</span>
              </div>
            ))}
          </div>
          <p className="helpsheet__line">
            {t('On Windows and Linux, use Ctrl wherever this list says Cmd.')}
          </p>
        </div>
      </Dialog>

      <Dialog
        open={sheet === 'contact'}
        onClose={closeSheet}
        title={t('Contact us')}
        description={t('We answer in Thai and in English, on weekdays, Bangkok time.')}
        footer={
          <Button variant="outline" onClick={closeSheet} data-autofocus>
            {t('Close')}
          </Button>
        }
      >
        <div className="helpsheet">
          <div className="helpsheet__pair">
            <span className="helpsheet__pair-label">{t('LINE')}</span>
            <CopyField label={t('LINE account')} value={SUPPORT.line} size="md" />
          </div>
          <div className="helpsheet__pair">
            <span className="helpsheet__pair-label">{t('Email')}</span>
            <CopyField label={t('Support email')} value={SUPPORT.email} size="md" />
          </div>
          <p className="helpsheet__line">
            {t('Tell us the name of the project you are asking about, and we can see what it is doing.')}
          </p>
        </div>
      </Dialog>
    </div>
  );
}
